import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SkillCategory from '../components/SkillCategory';
import PageHero from '../components/PageHero';
import { useTranslation } from 'react-i18next';

import skillsData from '../data/skills.json';

interface Skill {
  name: string;
  level: number;
  icon: string;
  description: string;
}

interface SkillCategoryData {
  category: string;
  skills: Skill[];
}

const Skills: React.FC = () => {
  const { t } = useTranslation('skills');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const skillCategories = skillsData as SkillCategoryData[];
  const allSkills = skillCategories.flatMap(cat => cat.skills);
  
  const categories = ['all', ...skillCategories.map(cat => cat.category)];
  
  const displayedSkills = selectedCategory === 'all' 
    ? allSkills 
    : skillCategories.find(cat => cat.category === selectedCategory)?.skills || [];

  return (
    <div className="min-h-screen pt-24">
      <section className="page-section relative overflow-hidden">
        <div className="relative z-10 container mx-auto px-6">
          <PageHero title={t('heroTitle')} subtitle={t('heroSubtitle')} />
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col items-center gap-6"
          >
            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`group relative px-6 py-3 rounded-xl font-medium transition-all duration-300 overflow-hidden ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-purple-200 shadow-lg shadow-primary-500/30'
                      : 'border border-primary-500/30 text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 hover:border-primary-500/50'
                  }`}
                >
                  <span className="relative z-10 flex items-center space-x-2">
                    {category === 'all' ? `🎯 ${t('allLabel')}` : t(`categories.${category}`, category)}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills Display */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          {selectedCategory === 'all' ? (
            <div className="space-y-16">
              {skillCategories.map((category, index) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <SkillCategory
                    category={category.category}
                    skills={category.skills}
                    viewMode={viewMode}
                    delay={index * 0.2}
                    onChangeViewMode={(mode) => setViewMode(mode as any)}
                    showToggle={index === 0}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <SkillCategory
                category={selectedCategory}
                skills={displayedSkills}
                viewMode={viewMode}
                onChangeViewMode={(mode) => setViewMode(mode as any)}
                showToggle={true}
              />
            </motion.div>
          )}
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 hidden">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">
              {t('ctaTitle')}
            </h2>
            <p className="text-xl text-dark-300 mb-8 leading-relaxed">
              {t('ctaDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl font-semibold text-purple-200 shadow-lg hover:shadow-xl transition-all duration-300">
                {t('ctaContact')}
              </button>
              <button className="px-8 py-4 border-2 border-primary-500 rounded-xl font-semibold text-primary-400 hover:bg-primary-500/10 transition-all duration-300">
                {t('ctaViewProjects') }
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Skills;